import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout, Button, Spin, Result } from 'antd';
import { 
  ArrowLeftOutlined, 
  HomeOutlined, 
  WarningOutlined
} from '@ant-design/icons';
import './IntroPage.css';
import './ConsentPage.css';

const { Content } = Layout;

const ConsentPage = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const testTypeText = type === 'self' ? '给自己测' : '为恋人测';
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // 检查sessionId
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      navigate('/');
      return;
    }

    // 检查是否已完成该类型的测试
    const completed = localStorage.getItem(`rpi_completed_${type}`);
    if (completed === 'true') {
      navigate(`/report/${type}`);
    }
  }, [type, navigate]);

  const handleAgree = () => {
    if (!agreed) {
      alert('请先勾选同意条款');
      return;
    }
    // 跳转到基本信息页面
    navigate(`/demographic/${type}`);
  };

  const handleDisagree = () => {
    navigate('/');
  };

  return (
    <Layout className="rpi-intro-layout">
      <header className="rpi-header">
        <div className="rpi-header-content">
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <HomeOutlined style={{ fontSize: 18, color: 'var(--rpi-muted-foreground)' }} />
              <span style={{ fontSize: 14, color: 'var(--rpi-muted-foreground)' }}>首页</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>{type === 'self' ? '💖' : '💞'}</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--rpi-primary)' }}>{testTypeText}</span>
            </div>
          </div>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/')}
          >
            返回
          </Button>
        </div>
      </header>

      <Content>
        <div className="rpi-consent-page">
          {/* Hero */}
          <div className="rpi-consent-page-hero">
            <h1>恋爱占有欲指数评估</h1>
            <p>基于心理学研究的专业测评工具</p>
          </div>

          {/* 主卡片 */}
          <div className="rpi-consent-main-card">
            <div className="rpi-consent-card-header">
              <span className="rpi-consent-card-icon">📋</span>
              <h2>知情同意书</h2>
            </div>

            {/* 评估目的 */}
            <div className="rpi-consent-item">
              <div className="rpi-consent-item-icon" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <span>🎯</span>
              </div>
              <div className="rpi-consent-item-content">
                <h3>评估目的</h3>
                <p>
                  本评估旨在帮助您了解自己或恋人在恋爱关系中的占有欲特征，包括控制倾向、嫉妒强度、情感依赖和关系不安全感等方面。这是一个基于心理学研究的自我探索工具，可以促进您对亲密关系动态的认知和理解，帮助建立更健康和谐的恋爱关系。
                </p>
              </div>
            </div>

            {/* 隐私保护 */}
            <div className="rpi-consent-item">
              <div className="rpi-consent-item-icon" style={{ background: 'rgba(233, 32, 99, 0.1)' }}>
                <span>🔒</span>
              </div>
              <div className="rpi-consent-item-content">
                <h3>隐私保护</h3>
                <p>
                  您的所有答案将<strong>完全匿名</strong>，仅在您的设备本地存储，
                  <strong>不会上传到任何服务器</strong>。我们不收集任何可识别个人身份的信息。您可以随时删除本地保存的数据，也可以选择导出数据用于个人记录。您的隐私和数据安全是我们的首要关注。
                </p>
              </div>
            </div>

            {/* 自愿参与 */}
            <div className="rpi-consent-item">
              <div className="rpi-consent-item-icon" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                <span>👥</span>
              </div>
              <div className="rpi-consent-item-content">
                <h3>自愿参与</h3>
                <p>
                  参与本评估完全基于自愿原则。您可以在任何时候选择退出评估，无需说明理由。对于任何让您感到不适的问题，您都可以选择跳过。评估过程中如果您感到不适，请立即停止并寻求专业心理健康服务的帮助。
                </p>
              </div>
            </div>

            {/* 非诊断性质 */}
            <div className="rpi-consent-item">
              <div className="rpi-consent-item-icon" style={{ background: 'rgba(255, 193, 7, 0.1)' }}>
                <span>⚠️</span>
              </div>
              <div className="rpi-consent-item-content">
                <h3>非诊断性质</h3>
                <p>
                  本评估结果仅供自我了解和关系反思使用，不能替代专业的心理咨询或情感咨询。如果您在恋爱关系中遇到严重困扰（如家庭暴力、严重控制等），建议立即寻求专业帮助。评估结果不应被用于指责伴侣或做出重大关系决策。
                </p>
              </div>
            </div>

            {/* 重要提醒 */}
            <div className="rpi-consent-alert">
              <span className="rpi-consent-alert-icon">⚠️</span>
              <div className="rpi-consent-alert-content">
                <strong>重要提醒：</strong>
                如果您目前正在经历严重的关系暴力、极端控制、或有自伤/伤人倾向，请立即寻求专业帮助（拨打紧急热线或就诊）。本评估不适合处于危机关系或心理危机状态的人群。
              </div>
            </div>

            {/* 总的同意确认 */}
            <div className="rpi-consent-final-check">
              <div 
                className={`rpi-consent-radio-large ${agreed ? 'checked' : ''}`}
                onClick={() => setAgreed(!agreed)}
              >
                <span className="rpi-consent-radio-circle">
                  {agreed && <span className="rpi-consent-radio-dot"></span>}
                </span>
                <span className="rpi-consent-radio-text">
                  我已仔细阅读并理解上述所有内容，自愿参与本次评估，同意将答案用于生成个人报告，并理解本测评的非诊断性质
                </span>
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="rpi-consent-page-actions">
            <Button
              size="large"
              onClick={handleDisagree}
              className="rpi-consent-page-btn-cancel"
            >
              我不同意
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleAgree}
              disabled={!agreed}
              className="rpi-consent-page-btn-submit"
            >
              我同意并开始评估
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default ConsentPage;
